import { Smartphone, FlaskConical } from "lucide-react";
import { useState, useEffect } from "react";

export default function IntegrationsSection() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const integrations = [
    {
      name: "Google Calendar",
      logo: "/images/logos/googlecalendar.png",
      alt: "Google Calendar",
      backgroundColor: "#ffffff",
      glowColor: "66, 133, 244",
      rounded: false,
    },
    {
      name: "Garmin",
      logo: "/images/logos/garmin.png",
      alt: "Garmin",
      backgroundColor: "#f8f9fa",
      glowColor: "0, 124, 195",
      rounded: true,
    },
    {
      name: "Strava",
      logo: "/images/logos/strava.png",
      alt: "Strava",
      backgroundColor: "#ffffff",
      glowColor: "252, 76, 2",
      rounded: false,
    },
    {
      name: "Apple Health",
      logo: "/images/logos/applehealth.png",
      alt: "Apple Health",
      backgroundColor: "#ffffff",
      glowColor: "255, 107, 107",
      rounded: true,
    },
    {
      name: "Fitbit",
      logo: "/images/logos/fitbit.png",
      alt: "Fitbit",
      backgroundColor: "#ffffff",
      glowColor: "0, 176, 185",
      rounded: true,
    },
    {
      name: "Oura",
      logo: "/images/logos/oura.png",
      alt: "Oura",
      backgroundColor: "#ffffff",
      glowColor: "108, 92, 231",
      rounded: true,
    },
    {
      name: "Whoop",
      logo: "/images/logos/whoop.png",
      alt: "Whoop",
      backgroundColor: "#f8f9fa",
      glowColor: "50, 50, 50",
      rounded: true,
    },
    {
      name: "MyFitnessPal",
      logo: "/images/logos/myfitnesspal.png",
      alt: "MyFitnessPal",
      backgroundColor: "#ffffff",
      glowColor: "0, 102, 204",
      rounded: false,
    },
  ];

  if (!mounted) return null;

  return (
    <section className="py-12 md:pt-16 md:pb-8 bg-secondary/50 border-y-2 border-border overflow-hidden">
      <div className="container mx-auto px-1">
        {/* Header */}
        <div className="text-center space-y-6 mb-8">
          <span className="text-primary text-sm font-bold tracking-wider uppercase">
            INTEGRATIONS
          </span>
          <h2 className="text-4xl md:text-5xl xl:text-6xl font-bold leading-tight text-foreground">
            Connect your existing{" "}
            <span className="text-primary">ecosystem</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Seamlessly integrate with the apps and devices you already use
          </p>

          {/* Integration Stats */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-8 pt-6 border-t border-border/30">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-foreground">
                500+ integrations available
              </span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-border"></div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-muted border border-border opacity-60 flex items-center justify-center rounded">
                  <Smartphone className="w-2.5 h-2.5 text-muted-foreground" />
                </div>
                <span className="text-sm text-muted-foreground">
                  Screen Time
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-muted border border-border opacity-60 flex items-center justify-center rounded">
                  <FlaskConical className="w-2.5 h-2.5 text-muted-foreground" />
                </div>
                <span className="text-sm text-muted-foreground">
                  Lab Results
                </span>
              </div>
              <span className="text-xs text-primary font-medium px-2 py-1 bg-primary/10 rounded-full">
                Coming Soon
              </span>
            </div>
          </div>
        </div>

        {/* Orbital Container */}
        <div className="relative w-full max-w-3xl mx-auto aspect-square">
          {/* Subtle Orbital Rings - Starting much closer */}
          <div className="absolute inset-72 rounded-full bg-gradient-to-r from-transparent via-gray-100/20 to-transparent blur-sm"></div>
          <div className="absolute inset-64 rounded-full bg-gradient-to-r from-transparent via-gray-200/20 to-transparent blur-sm"></div>
          <div className="absolute inset-48 rounded-full bg-gradient-to-r from-transparent via-gray-300/20 to-transparent blur-sm"></div>
          <div className="absolute inset-32 rounded-full bg-gradient-to-r from-transparent via-gray-400/10 to-transparent blur-sm"></div>
          <div className="absolute inset-24 rounded-full bg-gradient-to-r from-transparent via-gray-500/5 to-transparent blur-sm"></div>

          {/* Central Logo - No Circle */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
            <div className="w-32 h-32 md:w-40 md:h-40 flex items-center justify-center p-6">
              <img
                src="/images/logo-white.png"
                alt="1440 Logo"
                className="w-full h-full object-contain drop-shadow-lg"
              />
            </div>
          </div>

          {/* Orbiting Logos Container */}
          <div className="absolute inset-0 orbital-container">
            {integrations.map((integration, index) => {
              const rotation = index * 45; // 8 items, 360/8 = 45 degrees each

              return (
                <div
                  key={index}
                  className="orbital-item"
                  style={{
                    "--rotation": `${rotation}deg`,
                  }}
                >
                  <div className="orbital-logo">
                    <div
                      className={`w-20 h-20 md:w-22 md:h-22 hover:scale-105 transition-all duration-300 flex items-center justify-center p-3 relative shadow-lg ${
                        integration.rounded ? "rounded-2xl" : "rounded-lg"
                      } z-10`}
                      style={{
                        backgroundColor: integration.backgroundColor,
                        boxShadow: `0 0 25px rgba(${integration.glowColor}, 0.3), 0 8px 32px rgba(0, 0, 0, 0.1)`,
                      }}
                    >
                      <img
                        src={integration.logo}
                        alt={integration.alt}
                        className="w-full h-full object-contain"
                        style={{
                          filter:
                            integration.name === "Garmin"
                              ? "brightness(1.3) contrast(1.1)"
                              : integration.name === "Whoop"
                              ? "brightness(1.2)"
                              : "none",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                      {/* Fallback */}
                      <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-300 items-center justify-center text-xs font-bold text-gray-700 hidden rounded">
                        {integration.name
                          .split(" ")
                          .map((word) => word[0])
                          .join("")
                          .substring(0, 2)}
                      </div>

                      {/* Enhanced glow on hover */}
                      <div
                        className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-inherit"
                        style={{
                          boxShadow: `0 0 40px rgba(${integration.glowColor}, 0.5)`,
                        }}
                      ></div>
                    </div>

                    {/* Small connection line coming out of each logo */}
                    <div
                      className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 w-px h-8 opacity-60"
                      style={{
                        background: `linear-gradient(to bottom, rgba(255, 255, 255, 0.8), transparent)`,
                      }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <style jsx>{`
        .orbital-container {
          animation: rotate 30s linear infinite;
        }

        .orbital-item {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          transform-origin: 0 0;
          transform: translate(-50%, -50%) rotate(var(--rotation));
        }

        .orbital-logo {
          position: absolute;
          top: -240px;
          left: -36px;
          /* Remove counter-rotation to keep logos upright */
        }

        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 768px) {
          .orbital-logo {
            top: -200px;
            left: -32px;
          }
        }
      `}</style>
    </section>
  );
}
